import DownloadOutlined from "@ant-design/icons/lib/icons/DownloadOutlined";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Radio,
  Select,
  Table,
  Tooltip,
} from "antd";
import moment from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import accountApi from "../account/AccountApi";

moment.extend(utc);
moment.extend(timezonePlugin);
import React, { useEffect, useMemo, useState } from "react";
import {
  AccountBean,
  DspBean,
  DspReportKeyColumnDescription,
  DspReportValueColumnDescription,
  KeyFilterClause,
  PublisherBean,
  ScheduledReportRequestObject,
  ScheduledReportRequestReportRequestParametersObjectObject,
} from "src/api/api";
import { formDataLoader, FormDataType } from "src/util/FormDataLoader";
import { useUser } from "../../../context/UserContext";
import {
  beautifyReportData,
  isAdmin,
  isReporting,
  lowerize,
} from "../../../util/Util";
import { calculateColumnsWidth } from "../rawReports/dynamicColumnsHelper";
import reportApi from "./ReportApi";
import scheduledReportApi from "../scheduledReport/ScheduledReportApi";
import {
  buildFilteredReportTotalRow,
  getReportColumnLabel,
  getReportFilterableKeys,
  headerDescription,
  sortReportRows,
} from "./ReportUtil";
import ColumnFilterChecklist, { renderColumnFilterIcon } from "../ui/ColumnFilterChecklist";
import ColumnEnum = DspReportKeyColumnDescription.ColumnEnum;
import Papa from "papaparse";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";
const formInfoItems: FormDataType[] = [
  FormDataType.DspList,
  FormDataType.Publishers,
  FormDataType.DspReportColumns,
];

const DspKeyColumns = DspReportKeyColumnDescription.ColumnEnum;
const DspValueColumns = DspReportValueColumnDescription.ColumnEnum;

const FormItem = Form.Item;
const Option = Select.Option;

export const DspReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [form] = Form.useForm();

  const [blobCsv, setBlobCsv] = useState<any>();
  const [header, setHeader] = useState<string[]>();
  const [data, setData] = useState<string[][]>();
  const [scheduled, setScheduled] = useState<boolean>(false);
  const [totals, setTotals] = useState<{ [key: string]: number }>();
  const [sortConfig, setSortConfig] = useState<{ key: string | null; order: 'ascend' | 'descend' | null }>({ key: null, order: null });
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [hoveredSortCell, setHoveredSortCell] = useState<string | null>(null);
  const [hoveredFilter, setHoveredFilter] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  const [apiColumns, setApiColumns] = useState<string[]>([]);
  const [apiGroupBy, setApiGroupBy] = useState<string[]>([]);
  const [reportGroupBy, setReportGroupBy] = useState<string[]>([]);

  const { user } = useUser();
  const admin = isAdmin(user);
  const reporting = isReporting(user);



    const getKeyColumns = (cols: string[], formValues: any) => {

        const res: DspReportKeyColumnDescription[] = []
        res.push({ column: DspKeyColumns.Dsp })

        if (!cols) {
            return res
        }

        if (formValues.dsps && formValues.dsps.length > 0) {
            res.push({
                column: DspKeyColumns.DspId,
                where: {
                    type: KeyFilterClause.TypeEnum.In,
                    value: formValues.dsps
                },
                onlyFilter: true
            })
        } else {
            res.push({
                column: DspKeyColumns.DspId,
                where: {
                    type: KeyFilterClause.TypeEnum.In,
                    value: [-1, ...info.dspList.map((dsp: DspBean) => dsp.id)]
                },
                onlyFilter: true
            })
        }

        if (cols.findIndex(col => col === DspKeyColumns.Publisher.toString()) !== -1) {
            res.push({
                column: DspKeyColumns.Publisher,
            })
        }
        if (formValues.publishers && formValues.publishers.length > 0) {
            if (formValues.publishers.length > 1) {

                res.push({
                    column: DspKeyColumns.PublisherId,
                    where: {
                        type: KeyFilterClause.TypeEnum.In,
                        value: formValues.publishers
                    },
                    onlyFilter: true
                })
            } else if (formValues.publishers.length === 1) {
                res.push({
                    column: DspKeyColumns.PublisherId,
                    where: {
                        type: KeyFilterClause.TypeEnum.Equals,
                        value: formValues.publishers
                    },
                    onlyFilter: true
                })
            }
        }

        let dateCol: DspReportKeyColumnDescription
        dateCol = {
            column: formValues.groupingPeriod,
            where: {
                type: KeyFilterClause.TypeEnum.Between,
                value: formValues.dates.map(d => d.format(dateFormat))
            }
        }
        if (cols.findIndex(col => col === DspKeyColumns.Date.toString()) === -1) {
            dateCol.onlyFilter = true
        }
        res.push(dateCol)

        // Columns with special where clauses
        const specialWhereColumns: Record<string, any> = {
            [DspKeyColumns.Size.toString()]: {
                type: KeyFilterClause.TypeEnum.NotEquals,
                value: ''
            },
            [DspKeyColumns.MediaType.toString()]: {
                type: KeyFilterClause.TypeEnum.NotEquals,
                value: ''
            },
        };

        // Columns already handled above
        const handledKeyColumns = new Set([
            DspKeyColumns.Dsp.toString(),
            DspKeyColumns.DspId.toString(),
            DspKeyColumns.Publisher.toString(),
            DspKeyColumns.PublisherId.toString(),
            DspKeyColumns.Date.toString(),
            DspKeyColumns.FourHourly.toString(),
            DspKeyColumns.Daily.toString(),
            DspKeyColumns.Weekly.toString(),
            DspKeyColumns.Monthly.toString(),
            DspKeyColumns.Yearly.toString(),
        ]);

        const groupBySet = new Set(
            apiGroupBy.length > 0
                ? apiGroupBy
                : Object.keys(Object.assign({}, DspKeyColumns))
        );

        cols?.filter(col => groupBySet.has(col) && !handledKeyColumns.has(col))
            .forEach(col => {
                if (specialWhereColumns[col]) {
                    res.push({ column: col as any, where: specialWhereColumns[col] });
                } else {
                    res.push({ column: col as any });
                }
            });

        return res

  };

  const getValueColumns = (cols: string[], formValues: any) => {
    if (!cols) return [];
    const valueColumnSet = new Set(
      apiColumns.length > 0
        ? apiColumns
        : Object.keys(Object.assign({}, DspValueColumns))
    );
    return cols
      .filter((col) => valueColumnSet.has(col))
      .map((col) => ({ column: col as any }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setBlobCsv(undefined);
    setData(undefined);
    setTotals(undefined);
    setSortConfig({ key: null, order: null });
    setColumnFilters({});
    setReportGroupBy([]);
    setOpenFilterKey(null);
    const values = await form.validateFields();
    if (user?.role == AccountBean.RoleEnum.Dsp) {
      values.dsps = [user.dspId];
    }
    const cols = values.columns as string[];
    const groups = values.groups as string[];

    const allCols = ([] as string[]).concat(cols, groups);

    let keyCols = getKeyColumns(allCols, values);
    let valueCols = getValueColumns(allCols, values);

    if (scheduled) {
      await scheduledReportApi
        .createPublisher({
          requestParameters: {
            keyColumns: keyCols,
            valueColumns: valueCols,
            type: ScheduledReportRequestObject.ReportTypeEnum.ByDsp,
          },
          name: form.getFieldValue("name"),
          reportType: ScheduledReportRequestObject.ReportTypeEnum.ByDsp,
          nextReport: form
            .getFieldValue("nextReport")
            .format("YYYY-MM-DD HH:mm"),
          frequency: form.getFieldValue("frequency"),
        })
        .then((resp) => {
          message.success('Scheduled report created successfully.', 4);
          return resp.data;
        })
        .catch((err) => {
          onRejected(err);
        })
        .finally(() => setLoading(false));
      return;
    }

    let uuid: string | undefined;
    try {
      uuid = await reportApi
        .createDsp({
          keyColumns: keyCols,
          valueColumns: valueCols,
        })
        .then((resp) => resp.data);
    } catch (err) {
      onRejected(err);
      setLoading(false);
      return;
    }
    if (!uuid) {
      setLoading(false);
      return;
    }
    let interval = setInterval(async () => {
      try {
        const response = await reportApi.get(uuid ?? "");
        clearInterval(interval);
        const blob = await response.blob();
        const text = await blob.text();
    
        if (text.length < 1) return;
    
        const parsed = Papa.parse<string[]>(text, {
          skipEmptyLines: true,
            delimiter: ",", 

        });
    
const rows = parsed?.data ?? [];
    
    
        setHeader(rows[0]);
        setReportGroupBy(groups ?? []);
        setSortConfig({ key: rows[0]?.[0]?.toLowerCase() ?? null, order: 'ascend' });
        setData(rows.slice(1));
        setBlobCsv(blob as any);
        setLoading(false);
    
        if (rows.length > 1) {
          const reportInfo = await reportApi.info(uuid ?? "");
          const reportBean = reportInfo.data;
          setTotals(reportBean?.totalValues);
        }
      } catch (response) {
        if (typeof response === "object") {
          if (response.status !== 425) {
            setLoading(false);
            clearInterval(interval);
          }
        }
      }
    }, 500);
  };

  const [info, setInfo] = useState({
    dspList: [],
    publishers: [],
  });
  const [userTimezone, setUserTimezone] = useState<string>("");

  const onRejected = (err) => {
    setError(err.error);
  };

  const loadData = async () => {
    try {
      if (admin || reporting) {
        let formInfo = await formDataLoader(...formInfoItems);
        setInfo(formInfo as any);
        setApiColumns(formInfo.dspReportColumns?.columns ?? []);
        setApiGroupBy(formInfo.dspReportColumns?.groupBy ?? []);
      }
      const tz = user?.id
        ? (await accountApi.view(user.id))?.data?.timezoneId || (moment as any).tz.guess()
        : (moment as any).tz.guess();
      setUserTimezone(tz);
      const nowInUserTz = (moment() as any).tz(tz);
      const offsetDiff = nowInUserTz.utcOffset() - moment().utcOffset();
      form.setFieldsValue({ nextReport: moment().add(offsetDiff, "minute") });
    } catch (e) {
      onRejected(e);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.id]);

  useEffect(() => {
    if (scheduled && userTimezone) {
      const nowInUserTz = (moment() as any).tz(userTimezone);
      const offsetDiff = nowInUserTz.utcOffset() - moment().utcOffset();
      form.setFieldsValue({ nextReport: moment().add(offsetDiff, "minute") });
    }
  }, [scheduled]);

  useEffect(() => {
    beautifyReportData(header, data);
  }, [data]);

  const download = () => {
    // if (navigator.msSaveBlob) { // IE 10+
    //     navigator.msSaveBlob(blobCsv, 'report.csv');
    // } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blobCsv);
      link.setAttribute("href", url);
      link.setAttribute("download", "report.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    // }
  };

const handleColumnsSelect = (value: any) => {
  if (value.includes(DspReportValueColumnDescription.ColumnEnum.Requests)) {
    form.setFieldsValue({
      groups: form
        .getFieldValue("groups")
        ?.filter(
          (item) =>
            item != DspReportKeyColumnDescription.ColumnEnum.Size &&
            item != DspReportKeyColumnDescription.ColumnEnum.MediaType
        ),
    });
  }
};

const handleGroupsSelect = (value: any) => {
  if (
    value.includes(DspReportKeyColumnDescription.ColumnEnum.Size)  ||
    value.includes(DspReportKeyColumnDescription.ColumnEnum.MediaType)
  ) {
    form.setFieldsValue({
      columns: form
        .getFieldValue("columns")
        ?.filter(
          (item) =>
            item != DspReportValueColumnDescription.ColumnEnum.Requests
        ),
    });
  }
};

const columnsOptionDescriptionFunc = (key: string) => {
  if (key == DspReportValueColumnDescription.ColumnEnum.Requests.toString()) {
    return (
      "You can select " +
      DspReportValueColumnDescription.ColumnEnum.Requests.toString() +
      " only without " +
      DspReportKeyColumnDescription.ColumnEnum.Size.toString() +
      ", " +
      DspReportKeyColumnDescription.ColumnEnum.MediaType.toString() +
      " and "
    );
  }
  if (key == DspReportKeyColumnDescription.ColumnEnum.Size.toString()) {
    return (
      "You can select " +
      DspReportKeyColumnDescription.ColumnEnum.Size.toString() +
      " only without " +
      DspReportValueColumnDescription.ColumnEnum.Requests.toString()
    );
  }
  if (key == DspReportKeyColumnDescription.ColumnEnum.MediaType.toString()) {
    return (
      "You can select " +
      DspReportKeyColumnDescription.ColumnEnum.MediaType.toString() +
      " only without " +
      DspReportValueColumnDescription.ColumnEnum.Requests.toString()
    );
  }
  return key;
};

  const headerFunc = (headerToProcess?: any[]) => {
    const mobileAppIdIndex = headerToProcess?.findIndex(h => h.toLowerCase() === 'mobileappid') ?? -1;

    return headerToProcess?.map((h, index) => {
      const lowerKey = h.toLowerCase();
      const isDomainOrMobileApp = lowerKey === 'domain' || lowerKey === 'mobileappid';
      const isDomain = lowerKey === 'domain';
      const isBeforeMobileAppId = mobileAppIdIndex !== -1 && index < mobileAppIdIndex;

      const description = headerDescription.find((hd) => hd.h === h)?.d;

      const sortHint = sortConfig.key === lowerKey
        ? (sortConfig.order === 'ascend' ? 'Click to sort descending' : 'Click to sort ascending')
        : 'Click to sort ascending';
      const suppressSortTooltip =
        hoveredHeader === lowerKey ||
        hoveredFilter === lowerKey ||
        openFilterKey === lowerKey;

      return {
        title: (
          <Tooltip
            title={description ?? undefined}
            placement="topLeft"
            open={hoveredHeader === lowerKey}
          >
            <span
              onMouseEnter={() => setHoveredHeader(lowerKey)}
              onMouseLeave={() => setHoveredHeader(null)}
              onClick={(e) => e.stopPropagation()}
            >
              {h}
            </span>
          </Tooltip>
        ),
        dataIndex: lowerKey,
        key: lowerKey,
        width: isDomain ? 300 : (isBeforeMobileAppId ? 150 : undefined),
        ellipsis: isDomainOrMobileApp ? {
          showTitle: false,
        } : false,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: {
          title: suppressSortTooltip ? null : sortHint,
          align: { offset: [0, -25] },
          open: hoveredSortCell === lowerKey && !suppressSortTooltip,
          mouseLeaveDelay: 0,
          overlayStyle: { pointerEvents: 'none' },
        },
        onHeaderCell: () => ({
          onMouseEnter: () => setHoveredSortCell(lowerKey),
          onMouseLeave: () => setHoveredSortCell(null),
        }),
        render: isDomainOrMobileApp ? (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span style={{
              display: 'block',
              wordBreak: 'break-all',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {text}
            </span>
          </Tooltip>
        ) : undefined,
      };
    });
  };

 const sourceFunc = (
  dataToProcess?: any[],
  headerToProcess?: any[],
  totalsToProcess?: any
) => {
  const cleanValue = (key: string, value: string) => {
    const lowerKey = key?.toLowerCase() ?? "";

    if (
      typeof value === "string" &&
      (
        (
          (lowerKey.includes("date") || lowerKey.includes("time")) &&
          /^\d{1,4},\d{3}-\d{2}-\d{2}/.test(value)
        ) ||
        lowerKey.includes("seller account id")
      )
    ) {
      return value.replace(/,/g, "");
    }

    return value;
  };

  const source =
    dataToProcess?.map((row) => {
      const isTotalsRow = row[0] === "";
      const obj = headerToProcess?.reduce((o, key, index) => {
        const lowerKey = key?.toLowerCase() ?? "";
        const value =
          index === 0 && isTotalsRow
            ? "Total"
            : cleanValue(lowerKey, row[index]);
        return { ...o, [lowerKey]: value };
      }, {}) ?? {};
      return isTotalsRow ? { ...obj, _isTotalRow: true } : obj;
    }) ?? [];

  return source;
};



  const handleSelectAllColumns = (value: any) => {
    if (value && value.length && value.includes("__all")) {
      form.setFieldsValue({
        groups: form
          .getFieldValue("groups")
          ?.filter(
            (item) =>
              item != DspReportKeyColumnDescription.ColumnEnum.Size  &&
              item != DspReportKeyColumnDescription.ColumnEnum.MediaType
          ),
      });

      return [
        ...(apiColumns.length > 0 ? apiColumns : Object.keys(
          Object.assign({}, DspReportValueColumnDescription.ColumnEnum)
        ).filter(
          (key) =>
            ![
              DspReportValueColumnDescription.ColumnEnum.WinRate.toString(),
              DspReportValueColumnDescription.ColumnEnum.ClickThroughRate.toString(),
              DspReportValueColumnDescription.ColumnEnum.CostPerClick.toString(),
              DspReportValueColumnDescription.ColumnEnum.ViewThroughRate.toString(),
            ].includes(key)
        )),
      ];
    }
    return value;
  };

  const handleSelectAllGroups = (value: any) => {
    if (value && value.length && value.includes("__all")) {
      return [
        ...(apiGroupBy.length > 0 ? apiGroupBy : Object.keys(
          Object.assign({}, DspReportKeyColumnDescription.ColumnEnum)
        ).filter(
          (key) =>
            ![
              DspReportKeyColumnDescription.ColumnEnum.Dsp.toString(),
              DspReportKeyColumnDescription.ColumnEnum.DspId.toString(),
              DspReportKeyColumnDescription.ColumnEnum.PublisherId.toString(),
              DspReportKeyColumnDescription.ColumnEnum.MediaType.toString(),
              DspReportKeyColumnDescription.ColumnEnum.Size.toString(),
              // DspReportKeyColumnDescription.ColumnEnum.Business.toString(),
              DspReportKeyColumnDescription.ColumnEnum.FourHourly.toString(),
              DspReportKeyColumnDescription.ColumnEnum.Daily.toString(),
              DspReportKeyColumnDescription.ColumnEnum.Weekly.toString(),
              DspReportKeyColumnDescription.ColumnEnum.Monthly.toString(),
              DspReportKeyColumnDescription.ColumnEnum.Yearly.toString(),
            ].includes(key)
        )),
      ];
    }
    return value;
  };

  const processedSource = useMemo(() => {
    return sourceFunc(data, header, totals) ?? [];
  }, [data, header, totals]);

  const valueColumnNames = useMemo(
    () => (apiColumns.length > 0 ? apiColumns : Object.keys(DspValueColumns)),
    [apiColumns],
  );

  const FILTERABLE_KEYS = useMemo(
    () => getReportFilterableKeys(header, reportGroupBy),
    [header, reportGroupBy],
  );

  const columnFilteredSource = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
    if (activeFilters.length === 0) return processedSource;

    const filteredRows = processedSource
      .filter((r: any) => !r._isTotalRow)
      .filter((r: any) => activeFilters.every(([k, vals]) => vals.includes(String(r[k] ?? ''))));

    if (!header || filteredRows.length === 0) {
      return filteredRows;
    }

    const totalRow = buildFilteredReportTotalRow(
      header,
      filteredRows,
      valueColumnNames,
    );

    return [...filteredRows, totalRow];
  }, [processedSource, columnFilters, header, valueColumnNames]);

  const sortedSource = useMemo(
    () => sortReportRows(columnFilteredSource, sortConfig.key, sortConfig.order),
    [columnFilteredSource, sortConfig],
  );

  const filterOptionsByKey = useMemo(() => {
    const map: Record<string, string[]> = {};
    FILTERABLE_KEYS.forEach((k) => {
      map[k] = Array.from(
        new Set(
          processedSource
            .filter((r: any) => !r._isTotalRow)
            .map((r: any) => String(r[k] ?? ''))
            .filter((v: string) => v && v !== 'undefined')
        )
      ).sort();
    });
    return map;
  }, [processedSource, FILTERABLE_KEYS]);

  const tableColumns = useMemo(() => {
    const cols = calculateColumnsWidth(
      headerFunc(header) ?? [],
      sortedSource ?? [],
      2000,
    ).columns;
    return cols.map((col: any) => {
      const key = col.dataIndex as string;
      const enriched: any = {
        ...col,
        sortOrder: sortConfig.key === key ? sortConfig.order : null,
      };
      if (FILTERABLE_KEYS.has(key)) {
        const options = filterOptionsByKey[key] ?? [];
        const selected = columnFilters[key] ?? [];
        const label = getReportColumnLabel(header, key);
        enriched.filterDropdown = ({ confirm }: { confirm: () => void }) => (
          <ColumnFilterChecklist
            options={options}
            selectedValues={selected}
            searchPlaceholder={`Search ${label}...`}
            onApply={(values) =>
              setColumnFilters((prev) => ({ ...prev, [key]: values }))
            }
            onClose={confirm}
          />
        );
        enriched.filterIcon = renderColumnFilterIcon(
          label,
          selected.length > 0,
          (open) => setHoveredFilter(open ? key : null),
        );
        enriched.filterDropdownOpen = openFilterKey === key;
        enriched.onFilterDropdownOpenChange = (visible: boolean) => {
          setOpenFilterKey(visible ? key : null);
          if (visible) {
            setHoveredSortCell(null);
            setHoveredFilter(null);
          }
        };
      }
      return enriched;
    });
  }, [header, sortedSource, sortConfig, columnFilters, filterOptionsByKey, openFilterKey, FILTERABLE_KEYS, hoveredHeader, hoveredSortCell, hoveredFilter]);

  const tableWidth = useMemo(() => {
    return calculateColumnsWidth(
      headerFunc(header) ?? [],
      sortedSource ?? [],
      2000,
    ).tableWidth;
  }, [header, sortedSource]);

  return (
    <div>
      {loading}
      {error && (
        <div>
          <Alert message={error} type="error" />
          <br />
        </div>
      )}
      <Form
        style={{
          maxWidth: "1000px",
        }}
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 3 }}
        wrapperCol={{ span: 21 }}
      >
        <FormItem
          label="Period"
          tooltip="Select dates of the report and a grouping period"
          style={{ marginBottom: 0 }}
        >
          <FormItem
            name="dates"
            rules={[
              {
                required: true,
                message: "Please choose dates",
              },
            ]}
            style={{ display: "inline-block" }}
          >
            <RangePicker format="YYYY-MM-DD" />
          </FormItem>
          <span
            style={{
              display: "inline-block",
              lineHeight: "32px",
              textAlign: "left",
              margin: "0 10px 0 20px",
            }}
          >
            Group by:
          </span>

          <FormItem
            name="groupingPeriod"
            initialValue={ColumnEnum.Date}
            style={{ display: "inline-block" }}
          >
            <Radio.Group
              id={"radioButtonsWithoutLines"}
              buttonStyle="solid"
              defaultValue={ColumnEnum.Date}
              size={"middle"}
              style={{ margin: "0px 0px 0px 0px" }}
            >
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Date}
                value={ColumnEnum.Date}
              >
                1Hr
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.FourHourly}
                value={ColumnEnum.FourHourly}
              >
                4Hr
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Daily}
                value={ColumnEnum.Daily}
              >
                Daily
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Weekly}
                value={ColumnEnum.Weekly}
              >
                Weekly
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Monthly}
                value={ColumnEnum.Monthly}
              >
                Monthly
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Yearly}
                value={ColumnEnum.Yearly}
              >
                Yearly
              </Radio.Button>

              {/*{Object.keys(DateOption).map(option =>*/}
              {/*    <Radio.Button id={'radioButtonsWithoutLines'} style={{border: '0px'}} key={option}*/}
              {/*                  value={option}>{option}</Radio.Button>)}*/}
            </Radio.Group>
          </FormItem>
        </FormItem>

        {(admin || reporting) && (
          <FormItem
            name="dsps"
            label="Dsps"
            tooltip="Select dsps to generate report with. If none chosen, all dsps will be selected"
          >
            <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Select dsps"
              listHeight={200}
              optionFilterProp="children"
              dropdownAlign={{
                points: ['tl', 'bl'],
                offset: [0, 4],   
                overflow: {
                  adjustX: false,
                  adjustY: true, 
                },
              }}
            >
              <Option key={-1} value={-1}>
                House Bidder
              </Option>
              {info.dspList.map((item: DspBean) => (
                <Option key={item.id} value={item.id as number}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
        )}

        {(admin || reporting) && (
          <FormItem
            name="publishers"
            label="Publishers"
            tooltip="Select publishers to generate report with. If none chosen, all publishers will be selected"
          >
            <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Select publishers"
              listHeight={200}
              optionFilterProp="children"
              dropdownAlign={{
                points: ['tl', 'bl'],
                offset: [0, 4],   
                overflow: {
                  adjustX: false,
                  adjustY: true, 
                },
              }}
            >
              {info.publishers.map((item: PublisherBean) => (
                <Option key={item.id} value={item.id as number}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
        )}

        <FormItem
          name="columns"
          label="Columns"
          tooltip="Select columns to generate report with"
          getValueFromEvent={handleSelectAllColumns}
          rules={[
            {
              required: scheduled,
              message: "Please select columns",
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            mode="multiple"
            placeholder="Select columns"
            listHeight={200}
            onChange={handleColumnsSelect}
            dropdownAlign={{
              points: ['tl', 'bl'],
              offset: [0, 4],   
              overflow: {
                adjustX: false,
                adjustY: true, 
              },
            }}
          >
            {<Option value={"__all"}>{"Select all columns"}</Option>}
            {(apiColumns.length > 0 ? apiColumns : Object.keys(
              Object.assign({}, DspReportValueColumnDescription.ColumnEnum)
            )).map((key) => (
              <Option
                title={columnsOptionDescriptionFunc(key)}
                key={key}
                value={key}
              >
                {key}
              </Option>
            ))}
          </Select>
        </FormItem>

        <FormItem
          name="groups"
          label="Group by"
          tooltip="Select grouping values"
          getValueFromEvent={handleSelectAllGroups}
        >
          <Select
            showSearch
            allowClear
            mode="multiple"
            placeholder="Select grouping"
            listHeight={200}
            onChange={handleGroupsSelect}
            dropdownAlign={{
              points: ['tl', 'bl'],
              offset: [0, 4],   
              overflow: {
                adjustX: false,
                adjustY: true, 
              },
            }}
          >
            {<Option value={"__all"}>{"Select all grouping"}</Option>}
            {(apiGroupBy.length > 0 ? apiGroupBy : Object.keys(
              Object.assign({}, DspReportKeyColumnDescription.ColumnEnum)
            ).filter(
                (key) =>
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.DspId.toString() &&
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.PublisherId.toString() &&
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.Dsp.toString() &&
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.FourHourly.toString() &&
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.Daily.toString() &&
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.Weekly.toString() &&
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.Monthly.toString() &&
                  key !==
                    DspReportKeyColumnDescription.ColumnEnum.Yearly.toString()
              ))
              .map((key) => (
                <Option
                  title={columnsOptionDescriptionFunc(key)}
                  key={key}
                  value={key}
                >
                  {key}
                </Option>
              ))}
          </Select>
        </FormItem>

        {scheduled && (
          <div>
            <FormItem
              name="name"
              rules={[
                {
                  required: true,
                  message: "Please input scheduled report name",
                },
                {
                  max: 255,
                  message: "Name cannot be longer that 255 characters",
                },
              ]}
              label="Name"
              tooltip={"Name of scheduled report"}
            >
              <Input />
            </FormItem>

             <FormItem label="Date Time" style={{ marginBottom: 0 }}>
              <FormItem
                name="nextReport"
                rules={[
                  {
                    required: true,
                    message: "Please choose date",
                  },
                ]}
                getValueFromEvent={(val) => {
                  if (val && userTimezone) {
                    return (moment as any).tz(
                      val.format("YYYY-MM-DD HH:mm:ss"),
                      userTimezone
                    );
                  }
                  return val;
                }}
                style={{
                  display: "inline-block",
                  maxWidth: "calc(30%)",
                  minWidth: "180px",
                }}
              >
                <DatePicker
                  format="YYYY-MM-DD HH:mm:00"
                  showTime={{ format: "HH:mm" }}
                />
              </FormItem>
              <span
                style={{
                  display: "inline-block",
                  lineHeight: "32px",
                  textAlign: "left",
                  margin: "0 10px",
                }}
              >
                Frequency:
              </span>
              <FormItem
                name="frequency"
                rules={[
                  { required: true, message: "Please select report frequency" },
                ]}
                tooltip={"Each next report will be send in the same time."}
                initialValue={
                  ScheduledReportRequestReportRequestParametersObjectObject.FrequencyEnum.Daily
                }
                style={{
                  display: "inline-block",
                  minWidth: "100px",
                  maxWidth: "calc(30%)",
                }}
              >
                <Select placeholder="Select frequency">
                  {Object.keys(
                    ScheduledReportRequestReportRequestParametersObjectObject.FrequencyEnum
                  ).map((period) => (
                    <Option key={period} value={period}>
                      {period}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            </FormItem>
          </div>
        )}

        <FormItem
          wrapperCol={{
            offset: 3,
            span: 21,
          }}
        >
          <Button type="primary" htmlType="submit" loading={loading}>
            {scheduled ? "Create scheduled report" : "Generate report"}
          </Button>
          <Button
            style={{
              margin: "0 0 0 1%",
            }}
            type="default"
            htmlType="button"
            onClick={() => setScheduled(!scheduled)}
          >
            {scheduled ? "Generate" : "Schedule"}
          </Button>
        </FormItem>
      </Form>

      {blobCsv && (
        <Table
          pagination={false}
          scroll={{ x: tableWidth, y: 700 }}
          columns={tableColumns}
          dataSource={sortedSource}
          onChange={(_, __, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            setSortConfig(prev => {
              const clickedKey = (s?.columnKey ?? s?.field ?? prev.key) as string | null;
              if (!clickedKey) return prev;
              return { key: clickedKey, order: s?.order ?? 'ascend' };
            });
          }}
          // columns={
          //     header?.map(h => ({
          //         title: h,
          //         dataIndex: h.toLowerCase(),
          //         key: h.toLowerCase(),
          //     }))
          // }
          // dataSource={data?.map(row => header?.reduce((o, key, index) => ({
          //     ...o,
          //     [key?.toLowerCase() ?? '']: row[index],
          //     key: index
          // }), {}) ?? {})}
          footer={
            (data?.length ?? -1) > 0
              ? () => (
                  <Button onClick={download} icon={<DownloadOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}>
                    Download
                  </Button>
                )
              : undefined
          }
        />
      )}

    </div>
  );
};
